import React, { useState } from "react";
import { useIntl } from "react-intl";
import { ScenarioProperties } from "@/model";
import { isValidWebhookUrl } from "@/util/helper/settingHelper";
import { useAPIToken, useRefreshAPIToken } from "@/query/api";
import { useDialog } from "@/hook";
import { NumberInput, TextInputGroup, SwitchInput } from "@/component/input";
import Tooltip from "@/component/Tooltip";
import { errorToast, successToast } from "@/component/Toast";
import { PasteIcon } from "@/assets/svgs/icons";
import DoubleArrowRefreshIcon from "@/assets/svgs/refresh_double_arrows.svg";
import EyeOpenIcon from "@/assets/svgs/eye.svg";
import EyeCloseIcon from "@/assets/svgs/eye_close.svg";


interface ChattingInputGroupProps {
    properties: ScenarioProperties;
    onChange: (scenario: ScenarioProperties) => void;
}

const ChattingInputGroup: React.FC<ChattingInputGroupProps> = (props) => {
    const { properties, onChange } = props;

    const intl = useIntl();
    const dialog = useDialog();

    const [showToken, setShowToken] = useState<boolean>(false);

    const { data: token } = useAPIToken();
    const { mutateAsync: refreshToken } = useRefreshAPIToken();

    const updateNewToken = async () => {
        await refreshToken()
            .then(() => {
                successToast(intl.formatMessage({ id: "i10029" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "i10030" }));
            });
    };

    const onClickTokenRefresh = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (!token) {
            await updateNewToken();
            return;
        }

        dialog.open({
            title: intl.formatMessage({ id: "i10004" }),
            content: intl.formatMessage({ id: "i10033" }),
            variant: "danger",
            onConfirm: updateNewToken
        });
    };

    const onClickTokenCopy = (e: React.MouseEvent) => {
        e.preventDefault();

        navigator.clipboard.writeText(token!).then(() => {
            successToast(intl.formatMessage({ id: "i10034" }));
        });
    };

    return (
        <fieldset className="scenario-setting-card-input-group">
            <legend className="sr-only">
                {intl.formatMessage({ id: "b0033" })}
            </legend>
            <div className="flex flex-col gap-1.5">
                <label htmlFor="setting-api-timeout" className="field-label">
                    {intl.formatMessage({ id: "i10016" })}
                </label>
                <NumberInput id="setting-api-timeout"
                             min={1}
                             max={60}
                             value={properties.api_request_timeout}
                             onChange={e => onChange({ ...properties, api_request_timeout: Number(e.target.value) })}
                />
            </div>

            <div className="flex flex-col gap-1.5">
                <label htmlFor="setting-conv-expire" className="field-label">
                    {intl.formatMessage({ id: "i10017" })}
                </label>
                <NumberInput id="setting-conv-expire"
                             min={0}
                             max={Number.MAX_SAFE_INTEGER}
                             value={properties.conversation_expire_seconds}
                             onChange={e => onChange({ ...properties, conversation_expire_seconds: Number(e.target.value) })}
                />
            </div>

            <div className="flex flex-col gap-1.5">
                <label htmlFor="setting-last-conv-expire" className="field-label">
                    {intl.formatMessage({ id: "i10018" })}
                </label>
                <NumberInput id="setting-last-conv-expire"
                             min={0}
                             max={Number.MAX_SAFE_INTEGER}
                             value={properties.last_conversation_expire_seconds}
                             onChange={e => onChange({ ...properties, last_conversation_expire_seconds: Number(e.target.value) })}
                />
            </div>

            <div className="flex flex-col gap-1.5">
                <label htmlFor="setting-error-message" className="field-label">
                    {intl.formatMessage({ id: "i10019" })}
                </label>
                <input type="text"
                       id="setting-error-message"
                       className="input-default"
                       value={properties.error_message}
                       placeholder={intl.formatMessage({ id: "i10020" })}
                       onChange={e => onChange({ ...properties, error_message: e.target.value })}
                />
            </div>

            <div className="flex flex-col gap-1.5">
                <div className="flex gap-1 items-center">
                    <div className="label-input-grid-label field-label">
                        {intl.formatMessage({ id: "i10004" })}
                    </div>
                    <Tooltip message={intl.formatMessage({ id: "i10032" })} />
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-full">
                        <input type="text"
                               readOnly
                               tabIndex={-1}
                               aria-label={intl.formatMessage({ id: "i10004" })}
                               className="input-default"
                               defaultValue={(showToken || !token) ? token : "************************"}
                        />
                        <button className="absolute right-0 top-0 bottom-0 px-2"
                                aria-label={showToken ? intl.formatMessage({ id: "i10036" }) : intl.formatMessage({ id: "i10035" })}
                                onClick={() => setShowToken(!showToken)}
                        >
                            <img src={showToken ? EyeCloseIcon : EyeOpenIcon}
                                 alt=""
                                 className="size-4"
                            />
                        </button>
                    </div>
                    <button disabled={!token}
                            aria-label={intl.formatMessage({ id: "i10037" })}
                            onClick={onClickTokenCopy}
                    >
                        <PasteIcon className="size-4 text-black-400" />
                    </button>

                    <button aria-label={intl.formatMessage({ id: "i10038" })}
                            onClick={onClickTokenRefresh}
                    >
                        <img src={DoubleArrowRefreshIcon}
                             alt=""
                             className="size-4"
                        />
                    </button>
                </div>
            </div>

            <div className="flex flex-col gap-1.5">
                <label htmlFor="setting-webhook-url" className="field-label">
                    {intl.formatMessage({ id: "i10027" })}
                </label>
                <TextInputGroup id="setting-webhook-url"
                                value={properties.webhook_url}
                                placeholder={intl.formatMessage({ id: "i10028" })}
                                invalid={!isValidWebhookUrl(properties.webhook_url).isValid}
                                invalidText={intl.formatMessage({ id: "i10023" })}
                                onChange={value => onChange({ ...properties, webhook_url: value })}
                />
            </div>

            <div className="flex flex-col gap-2">
                <div className="h-input-default flex items-center justify-between">
                    <label htmlFor="setting-restore-context"
                           className="field-label"
                    >
                        {intl.formatMessage({ id: "i10021" })}
                    </label>
                    <SwitchInput id="setting-restore-context"
                                 value={properties.enable_restore_context}
                                 onSwitch={v => onChange({ ...properties, enable_restore_context: v })}
                    />
                </div>

                <div className="h-input-default flex items-center justify-between">
                    <label htmlFor="setting-debug"
                           className="field-label"
                    >
                        {intl.formatMessage({ id: "i10022" })}
                    </label>
                    <SwitchInput id="setting-debug"
                                 value={properties.enable_debug}
                                 onSwitch={v => onChange({ ...properties, enable_debug: v })}
                    />
                </div>
            </div>
        </fieldset>
    );
};

export default ChattingInputGroup;
